import { useLazyRef } from "@faro-lotv/app-component-toolbox";
import { useCallback } from "react";

/**
 * @param key A unique identifier for the thing to track changes of.
 * @param value The current value of the object.
 * @param comparisonFn Function to use to test for equality. Uses strict equality by default.
 * @returns `true` if the value changed since the last execution, else `false`.
 */
type HasChangedFn = (
  key: string,
  value: unknown,
  comparisonFn?: (a: unknown, b: unknown) => boolean,
) => boolean;

/**
 * @returns A function to track changes across multiple executions.
 * Allows reacting to changes more granularly, e.g. inside `useFrame` or in `useEffect` without modifying the dependency array.
 * @example
 * const hasChanged = useChangeCache();
 * const myValue = useAppSelector(selectMyValue());
 * const myOtherValue = useAppSelector(selectMyOtherValue());
 *
 * useFrame(() => {
 *   // Only execute expensive operation when necessary, but inside Three JS frame loop
 *   if (hasChanged("myValue", myValue)) {
 *     updateGeometry(myValue);
 *   }
 * });
 *
 * useEffect(() => {
 *   // Only execute when myOtherValue has changed, while satisfying React's effect dependency rules
 *   if (hasChanged("myOtherValue", myOtherValue)) {
 *     doThing(myValue, myOtherValue);
 *   }
 * }, [myValue, myOtherValue]);
 */
export function useChangeCache(): HasChangedFn {
  const changeCache = useLazyRef<Record<string, unknown>>(() => ({}));

  /**
   * @param key a unique identifier for the thing to track changes for.
   */
  const hasChanged: HasChangedFn = useCallback(
    (key, value, comparisonFn = (a: unknown, b: unknown) => a === b) => {
      const previous = changeCache.current[key];

      const hasChanged =
        !(key in changeCache.current) || !comparisonFn(previous, value);

      if (hasChanged) {
        changeCache.current[key] = value;
      }

      return hasChanged;
    },
    [changeCache],
  );

  return hasChanged;
}
